import React from 'react'
import Select, { components } from 'react-select'
import DateRangeComp from '../../../../components/dateComponent/DateRangeComp'
import moment from 'moment'
import { Tooltip } from 'react-tooltip'
import { KTSVG } from '../../../../_metronic/helpers'
import { SelectOption } from '../types'

const { Option } = components

const FILTER_CONTROL_HEIGHT = 38

const CheckboxOption = (props: any) => (
  <Option {...props} className='d-flex align-items-start gap-2'>
    <input type='checkbox' checked={props.isSelected} onChange={() => null} className='me-2 mt-1' />
    <label className='mb-0'>{props.label}</label>
  </Option>
)

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: FILTER_CONTROL_HEIGHT,
    height: 'auto',
    fontSize: '0.8125rem',
    borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
    boxShadow: 'none',
    '&:hover': { borderColor: '#cbd5e0' },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '2px 10px',
    minHeight: FILTER_CONTROL_HEIGHT - 2,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    minHeight: FILTER_CONTROL_HEIGHT,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#718096',
    fontSize: '0.8125rem',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#1a202c',
    fontSize: '0.8125rem',
  }),
  multiValue: (base: any) => ({
    ...base,
    fontSize: '0.75rem',
  }),
  menu: (base: any) => ({
    ...base,
    fontSize: '0.8125rem',
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 10050,
  }),
}

const selectMenuPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed' as const,
}

export type AnomalyFiltersState = {
  managers: SelectOption[]
  users: SelectOption[]
  categories: SelectOption[]
  periodPreset: '3' | '6' | 'custom'
  startDate: string
  endDate: string
  thresholdPercent: number
  showOnlyAnomalies: boolean
}

type Props = {
  value: AnomalyFiltersState
  onChange: (next: AnomalyFiltersState) => void
  managerOptions: SelectOption[]
  userOptions: SelectOption[]
  categoryOptions: SelectOption[]
  hideManager?: boolean
  embedded?: boolean
  onClear: () => void
  onRun: () => void
  running?: boolean
}

const AnomalyFilters: React.FC<Props> = ({
  value,
  onChange,
  managerOptions,
  userOptions,
  categoryOptions,
  hideManager,
  embedded,
  onRun,
  running,
}) => {
  const dateDisplay =
    value.startDate && value.endDate
      ? `${moment(value.startDate).format('DD-MM-YYYY')} - ${moment(value.endDate).format('DD-MM-YYYY')}`
      : ''

  const setPreset = (preset: '3' | '6') => {
    const end = moment()
    const start = moment().subtract(Number(preset) - 1, 'months').startOf('month')
    onChange({
      ...value,
      periodPreset: preset,
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    })
  }

  const filtersRow = (
    <>
      <Tooltip id='anomaly-threshold-tip' place='top' />
      <div
        className={`ss-anomalies-filters-row${
          hideManager ? ' ss-anomalies-filters-row--no-manager' : ''
        }`}
      >
          {!hideManager && (
            <div className='ss-anomalies-filter-field ss-anomalies-filter-field--manager'>
              <label className='form-label mb-1'>Manager</label>
              <Select
                isMulti
                options={managerOptions}
                value={value.managers}
                onChange={(opts) => onChange({ ...value, managers: (opts as SelectOption[]) || [] })}
                placeholder='All Managers'
                classNamePrefix='react-select'
                className='ss-anomalies-filter-select'
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option: CheckboxOption }}
                styles={selectStyles}
                {...selectMenuPortalProps}
              />
            </div>
          )}

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--user'>
            <label className='form-label mb-1'>{hideManager ? 'Team Member' : 'User'}</label>
            <Select
              isMulti
              options={userOptions}
              value={value.users}
              onChange={(opts) => onChange({ ...value, users: (opts as SelectOption[]) || [] })}
              placeholder='Search user...'
              classNamePrefix='react-select'
              className='ss-anomalies-filter-select'
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option: CheckboxOption }}
              styles={selectStyles}
              {...selectMenuPortalProps}
            />
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--category'>
            <label className='form-label mb-1'>Category</label>
            <Select
              isMulti
              options={categoryOptions}
              value={value.categories}
              onChange={(opts) => onChange({ ...value, categories: (opts as SelectOption[]) || [] })}
              placeholder='All Categories'
              classNamePrefix='react-select'
              className='ss-anomalies-filter-select'
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option: CheckboxOption }}
              styles={selectStyles}
              {...selectMenuPortalProps}
            />
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--period'>
            <label className='form-label mb-1'>Period</label>
            <div className='ss-anomalies-period-controls'>
              <div className='btn-group ss-anomalies-period-toggle' role='group' aria-label='Period preset'>
                <button
                  type='button'
                  className={`btn btn-sm ss-anomalies-period-btn ${
                    value.periodPreset === '3' ? 'btn-primary' : 'btn-light'
                  }`}
                  onClick={() => setPreset('3')}
                >
                  Last 3 Months
                </button>
                <button
                  type='button'
                  className={`btn btn-sm ss-anomalies-period-btn ${
                    value.periodPreset === '6' ? 'btn-primary' : 'btn-light'
                  }`}
                  onClick={() => setPreset('6')}
                >
                  Last 6 Months
                </button>
              </div>
              <DateRangeComp
                label=''
                className='ss-anomalies-period-date mb-0'
                startDate={value.startDate ? moment(value.startDate) : moment().subtract(5, 'months')}
                endDate={value.endDate ? moment(value.endDate) : moment()}
                value={dateDisplay}
                onApply={(_e: any, picker: any) => {
                  onChange({
                    ...value,
                    periodPreset: 'custom',
                    startDate: picker.startDate.format('YYYY-MM-DD'),
                    endDate: picker.endDate.format('YYYY-MM-DD'),
                  })
                }}
                onCancel={() => {
                  setPreset('6')
                }}
              />
            </div>
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--threshold'>
            <label className='form-label mb-1 d-flex align-items-center gap-1'>
              Anomaly Threshold
              <span
                className='ss-anomalies-threshold-info'
                data-tooltip-id='anomaly-threshold-tip'
                data-tooltip-content='Month-over-month increase above this % is flagged as an anomaly.'
                aria-label='Threshold help'
              >
                <KTSVG path='/media/icons/duotune/general/gen045.svg' className='svg-icon-5' />
              </span>
            </label>
            <div className='input-group input-group-sm ss-anomalies-threshold-input'>
              <input
                type='number'
                className='form-control form-control-solid'
                min={0}
                max={500}
                value={value.thresholdPercent}
                onChange={(e) =>
                  onChange({ ...value, thresholdPercent: Number(e.target.value) || 0 })
                }
              />
              <span className='input-group-text'>%</span>
            </div>
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--run'>
            <button
              type='button'
              className='btn btn-primary btn-sm fw-semibold ss-anomalies-run-btn'
              onClick={onRun}
              disabled={running}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3' />
              {running ? 'Running…' : 'Run Report'}
            </button>
          </div>
        </div>
    </>
  )

  if (embedded) {
    return <div className='ss-anomalies-filters-panel pb-4 mb-4 border-bottom'>{filtersRow}</div>
  }

  return (
    <div className='card ss-anomalies-filters-card mb-4'>
      <div className='card-body py-3 px-4'>{filtersRow}</div>
    </div>
  )
}

export default AnomalyFilters
